import React, { useState, useEffect, useCallback } from 'react';
import { BsCheckCircle, BsX } from 'react-icons/bs';
import { useToast } from '../../../hooks/useToast';
import { useAuth } from '../../../hooks/authHook';
import tournamentService, { Match } from '../../../services/tournamentService';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { ErrorMessage } from '../../../components/ui/ErrorMessage';

interface PendingMatchesProps {
  tournamentId?: number;
}

export const PendingMatches: React.FC<PendingMatchesProps> = ({ tournamentId }) => {
  const { success, error: showError } = useToast();
  const { user } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para el modal de reportar resultado
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [submittingResult, setSubmittingResult] = useState(false);
  const [resultForm, setResultForm] = useState({
    player1_score: 0,
    player2_score: 0,
  });

  const fetchPendingMatches = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Si se especifica un tournamentId, obtener partidas de ese torneo específico
      // Si no, obtener todas las partidas pendientes del usuario
      let pendingMatches: Match[];
      pendingMatches = await tournamentService.getPendingMatches(tournamentId ? tournamentId : null);
      setMatches(pendingMatches);
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar partidas pendientes';
      setError(errorMessage);
      console.error('Error loading pending matches:', err);
    } finally {
      setLoading(false);
    }
  }, [tournamentId, user]);

  useEffect(() => {
    if (user) {
      fetchPendingMatches();
    } else {
      setLoading(false);
    }
  }, [fetchPendingMatches, user]);

  const getOpponent = (match: Match) => {
    if (!user) return null;
    
    // Determinar quién es el oponente basado en el ID del usuario actual
    // Ahora podemos comparar directamente user.id con player.id (que viene de user_id en la API)
    if (match.player1?.id === user.id) {
      return match.player2;
    } else if (match.player2?.id === user.id) {
      return match.player1;
    }
    return null;
  };

  const handleReportResult = (match: Match) => {
    setSelectedMatch(match);
    setResultForm({ player1_score: 0, player2_score: 0 });
    setShowResultModal(true);
  };

  const handleSubmitResult = async () => {
    if (!selectedMatch) return;

    try {
      setSubmittingResult(true);
      
      // Usar el tournament_id del match directamente
      const matchTournamentId = selectedMatch.tournament_id;
      
      if (!matchTournamentId) {
        showError('Error', 'No se pudo identificar el torneo para reportar el resultado');
        return;
      }
      
      await tournamentService.setMatchResult(matchTournamentId, selectedMatch.id, {
        player1_score: resultForm.player1_score,
        player2_score: resultForm.player2_score
      });

      // Recargar las partidas pendientes
      await fetchPendingMatches();
      
      // Cerrar modal y resetear formulario
      setShowResultModal(false);
      setSelectedMatch(null);
      setResultForm({ player1_score: 0, player2_score: 0 });
      
      success('¡Resultado reportado!', 'El resultado de la partida ha sido registrado correctamente');
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al reportar resultado';
      showError('Error', errorMessage);
    } finally {
      setSubmittingResult(false);
    }
  };

  const handleCloseModal = () => {
    setShowResultModal(false);
    setSelectedMatch(null);
    setResultForm({ player1_score: 0, player2_score: 0 });
  };

  if (loading) {
    return <LoadingSpinner message="Cargando partidas pendientes..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchPendingMatches} />;
  }

  if (!user) {
    return (
      <div className="text-center py-8">
        <BsX className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          Debes estar logueado
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Inicia sesión para ver tus partidas pendientes.
        </p>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="text-center py-8">
        <BsCheckCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No tienes partidas pendientes
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {tournamentId 
            ? 'No tienes partidas asignadas en este torneo o todas están completadas.'
            : 'Todas tus partidas están completadas o aún no tienes partidas asignadas.'
          }
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-900">
          Mis Partidas Pendientes
        </h4>
        <span className="text-sm text-gray-500">
          {matches.length} {matches.length === 1 ? 'partida pendiente' : 'partidas pendientes'}
        </span>
      </div>

      <div className="space-y-3">
        {matches.map((match) => {
          const opponent = getOpponent(match);
          return (
            
            <div key={match.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Ronda {match.round}
                      </span>
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          vs {opponent ? `${opponent.first_name} ${opponent.last_name}`.trim() : 'BYE'}
                        </span>
                        {match.table_number && (
                          <span className="text-xs text-gray-500">
                            Mesa {match.table_number}
                          </span>
                        )}
                      </div>
                      
                      {opponent && (
                        <p className="text-xs text-gray-500">
                          @{opponent.username}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                    <span>Estado: {match.status === 'pending' ? 'Pendiente' : 'En progreso'}</span>
                    {(match.player1_games > 0 || match.player2_games > 0) && (
                      <span>
                        Score actual: {match.player1_games}-{match.player2_games}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex-shrink-0">
                  {opponent ? (
                    <button
                      className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      onClick={() => handleReportResult(match)}
                    >
                      Reportar Resultado
                    </button>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                      Victoria por BYE
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="text-center">
        <button
          onClick={fetchPendingMatches}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          🔄 Actualizar partidas
        </button>
      </div>

      {/* Modal para reportar resultado */}
      {showResultModal && selectedMatch && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Reportar Resultado
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <BsX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Ronda {selectedMatch.round}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  {selectedMatch.player1?.first_name} {selectedMatch.player1?.last_name} vs {selectedMatch.player2?.first_name} {selectedMatch.player2?.last_name}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Juegos ganados por {selectedMatch.player1?.first_name} {selectedMatch.player1?.last_name}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={resultForm.player1_score}
                    onChange={(e) => setResultForm({
                      ...resultForm,
                      player1_score: parseInt(e.target.value) || 0
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Juegos ganados por {selectedMatch.player2?.first_name} {selectedMatch.player2?.last_name}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={resultForm.player2_score}
                    onChange={(e) => setResultForm({
                      ...resultForm,
                      player2_score: parseInt(e.target.value) || 0
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex space-x-3">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitResult}
                  disabled={submittingResult}
                  className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {submittingResult ? 'Enviando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
